import React, { RefObject, useEffect, useMemo, useState } from 'react';

import SearchIcon from '../../../img/search-icon.svg';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';

import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
import { PartLogAction, PartLogsFilters, Part, PartLog } from '../../../interfaces/Parts';
import { usePartLogs, usePartsSearch } from '../../../infrastructure/react-query/hooks/usePartLogsQueries';
import { notify } from '../../../shared/feedback/notify';

import '../../../shared/feedback/erro-de-tela.css';
import './PartLogs.css';

interface PartLogsProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

const ACTION_LABEL: Record<PartLogAction, string> = {
  CREATED: 'Criado',
  UPDATED: 'Atualizado',
  ASSIGNED: 'Atribuído',
  UNASSIGNED: 'Desvinculado',
  LINKED_TO_CAR: 'Vinculado ao carro',
  UNLINKED_FROM_CAR: 'Desvinculado do carro',
  DELETED: 'Deletado',
};

const ERRO_PECAS = 'Não foi possível carregar a lista de peças. Recarregue a página; se persistir, acione a TI.';
const ERRO_LOGS = 'Não foi possível carregar o histórico desta peça. Recarregue a página; se persistir, acione a TI.';

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que esta tela mostra',
    conteudo: (
      <>
        <p>
          O histórico de alterações de uma peça: cada linha registra o que aconteceu
          (<strong>Evento</strong>), quem fez (<strong>Quem fez</strong>), quando
          (<strong>Data/Hora</strong>), o veículo envolvido (<strong>Carro</strong>) e o motivo
          informado, quando houver.
        </p>
        <p>
          Os registros são gravados pelo sistema a cada ação sobre a peça e não podem ser
          editados nem apagados — esta tela é somente consulta.
        </p>
      </>
    ),
  },
  {
    titulo: 'Como consultar',
    conteudo: (
      <>
        <p>
          1. Digite o nome no campo <strong>Buscar peça por nome</strong> e clique na lupa (ou
          pressione Enter).
        </p>
        <p>
          2. Escolha a peça no seletor <strong>Selecione uma peça</strong> — o histórico é
          sempre de uma peça por vez.
        </p>
        <p>
          3. Refine por período (<strong>Início</strong> e <strong>Fim</strong>) e por tipo de
          evento. <strong>Limpar</strong> desfaz os filtros.
        </p>
        <p>
          Enquanto nenhuma peça estiver escolhida, a tabela fica vazia — isso não significa que
          a peça não tenha histórico.
        </p>
      </>
    ),
  },
  {
    titulo: 'Os tipos de evento',
    conteudo: (
      <>
        <p>
          <strong>Criado</strong> e <strong>Atualizado</strong> — cadastro e alterações de dados
          da peça. A coluna <strong>Detalhes</strong> mostra o campo alterado, com o valor
          anterior e o novo.
        </p>
        <p>
          <strong>Vinculado ao carro</strong> e <strong>Desvinculado do carro</strong> — entrada
          e saída da peça em um atendimento.
        </p>
        <p>
          <strong>Atribuído</strong> e <strong>Desvinculado</strong> — definição e remoção da
          pessoa responsável pela peça. Não confundir <strong>Desvinculado</strong> (saiu o
          responsável, a peça segue no atendimento) com{' '}
          <strong>Desvinculado do carro</strong> (a peça saiu do atendimento).
        </p>
        <p>
          <strong>Deletado</strong> — remoção da peça.
        </p>
      </>
    ),
  },
];

const PartLogs: React.FC<PartLogsProps> = () => {
  const [partSearchTerm, setPartSearchTerm] = useState('');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [selectedPartId, setSelectedPartId] = useState<string>('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [filters, setFilters] = useState<PartLogsFilters>({
    startDate: '',
    endDate: '',
    action: '',
  });

  const partsQuery = usePartsSearch(partSearchTerm, true);

  const partLogsQuery = usePartLogs(
    selectedPartId || null,
    {
      page,
      limit,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      action: filters.action || undefined,
    },
  );

  const loading = partsQuery.isLoading || partLogsQuery.isLoading;
  const parts: Part[] = useMemo(() => partsQuery.data?.parts || [], [partsQuery.data]);
  const logs: PartLog[] = partLogsQuery.data?.logs || [];

  /**
   * at-69 (BUG-V84): a tela não tinha NENHUM tratamento de erro — nem toast, nem faixa.
   *
   * As duas consultas falhando devolviam coleção vazia, e a tela dizia "Selecione uma peça"
   * (sem nenhuma peça no seletor) e "Nenhum evento encontrado para os filtros informados".
   * As duas frases são afirmações de negócio, e nenhuma delas podia ser feita: a consulta
   * não respondeu (regra da at-60).
   */
  /**
   * ⚠️ Texto FIXO, não `errorFrom` (regra da at-65): estes avisos existem para dizer o que a
   * tela pode estar mostrando de errado, e nesse caso o motivo HTTP substitui a informação
   * útil. Aqui é pior que o 403 citado na regra — a camada de API embrulha a falha num
   * `Error('Erro ao buscar logs da peça')`, então `errorFrom` exibiria uma frase de
   * programador que não diz ao usuário nem o que fazer nem o que desconfiar.
   */
  const erroPecas = partsQuery.isError ? ERRO_PECAS : null;
  const erroLogs = partLogsQuery.isError ? ERRO_LOGS : null;

  useEffect(() => {
    if (partsQuery.isError) notify.error(ERRO_PECAS, { toastId: 'part-logs-pecas' });
  }, [partsQuery.isError]);

  useEffect(() => {
    if (partLogsQuery.isError) notify.error(ERRO_LOGS, { toastId: 'part-logs-historico' });
  }, [partLogsQuery.isError]);

  const selectedPart = useMemo(
    () => parts.find((part) => part.id === selectedPartId),
    [parts, selectedPartId],
  );

  const total = partLogsQuery.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const startIndex = total === 0 ? 0 : (page - 1) * limit + 1;
  const endIndex = Math.min(page * limit, total);

  const handleSearch = () => {
    setPage(1);
    partsQuery.refetch();
    if (selectedPartId) {
      partLogsQuery.refetch();
    }
  };

  const metadataSummary = (metadata?: Record<string, any> | null) => {
    if (!metadata) return '-';

    if (metadata.field) {
      return `${metadata.field}: ${String(metadata.before ?? '-')} -> ${String(metadata.after ?? '-')}`;
    }

    if (metadata.name) {
      return `Peça: ${metadata.name}`;
    }

    return JSON.stringify(metadata);
  };

  return (
    <div
      className="Frame1000004012"
      style={{
        height: loading ? '100vh' : 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: loading ? 'center' : 'space-between',
        alignItems: loading ? 'center' : 'flex-start',
        backgroundColor: loading ? '#808080' : 'transparent',
      }}
    >
      {loading && <FullScreenLoader />}

      {!loading && (
        <div className="ConteDo part-logs-page" style={{ flex: '1 1 0', paddingLeft: 'clamp(24px, 5vw, 64px)', paddingRight: 82, paddingTop: 80, background: 'var(--tema-superficie, white)', flexDirection: 'column', display: 'flex', minHeight: '100vh', overflowY: 'auto' }}>
          <div className="Topo">
            <div className="Frame1000003980">
              <div className="Frame1000003983">
                <div className="ItemSearch">
                  <img className="imgIcon" src={SearchIcon} alt="Icone de busca" width="20" height="20" onClick={handleSearch} />
                </div>
                <div className="PesquisePorPlacaChassiOuNDoEvento">
                  <input
                    type="text"
                    className="PesquiseInput"
                    value={partSearchTerm}
                    onChange={(e) => setPartSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Buscar peça por nome"
                  />
                </div>
              </div>
            </div>
            <div className="Camada1" />
          </div>

          <div className="TTuloRecepcao">
            <div className="part-logs-title">Log de Peças</div>
            <button
              type="button"
              className="help-circle-btn"
              title="Como Funciona"
              onClick={() => setIsHelpOpen(true)}
            >
              ?
            </button>
          </div>

          {/*
            A faixa NÃO substitui a tela: busca, filtros e o cabeçalho da tabela continuam
            renderizando abaixo dela (regra do BUG-V27, at-64).
          */}
          {erroPecas && <div className="erro-de-tela" role="alert">{erroPecas}</div>}
          {erroLogs && <div className="erro-de-tela" role="alert">{erroLogs}</div>}

          <div className="VisualizarOrdenar">
            <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex', flexWrap: 'wrap' }}>
              <div className="CtaBig part-logs-filter">
                <div className="Frame1000003987">
                  <select
                    className="ComponentNameImgIcon"
                    value={selectedPartId}
                    onChange={(e) => {
                      setSelectedPartId(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">Selecione uma peça</option>
                    {parts.map((part) => (
                      <option key={part.id} value={part.id}>{part.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="CtaBig part-logs-filter">
                <div className="Frame1000003987">
                  <div className="ComponentName">Início</div>
                  <input
                    className="ComponentNameImgIcon"
                    type="date"
                    value={filters.startDate || ''}
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, startDate: e.target.value }));
                      setPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="CtaBig part-logs-filter">
                <div className="Frame1000003987">
                  <div className="ComponentName">Fim</div>
                  <input
                    className="ComponentNameImgIcon"
                    type="date"
                    value={filters.endDate || ''}
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, endDate: e.target.value }));
                      setPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="CtaBig part-logs-filter">
                <div className="Frame1000003987">
                  <select
                    className="ComponentNameImgIcon"
                    value={filters.action || ''}
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, action: e.target.value as PartLogAction | '' }));
                      setPage(1);
                    }}
                  >
                    <option value="">Todos os eventos</option>
                    {Object.keys(ACTION_LABEL).map((action) => (
                      <option key={action} value={action}>{ACTION_LABEL[action as PartLogAction]}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button type="button" className="CtaBig part-logs-action" onClick={handleSearch}>
                <div className="Frame1000003987">
                  <div className="ComponentName">Buscar</div>
                </div>
              </button>

              <button
                type="button"
                className="CtaBig part-logs-clear"
                onClick={() => {
                  setPartSearchTerm('');
                  setSelectedPartId('');
                  setFilters({ startDate: '', endDate: '', action: '' });
                  setPage(1);
                }}
              >
                <div className="Frame1000003987">
                  <div className="ComponentName">Limpar</div>
                </div>
              </button>
            </div>
          </div>

          <div className="QuantidadeDeCarros">
            <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex' }}>
              <div className="CtaQuantidadeDeCarros">
                <div className="Frame1000003987">
                  <div className="ComponentName">
                    {erroLogs && selectedPartId
                      ? 'Não foi possível carregar o histórico'
                      : selectedPart
                        ? `${startIndex}-${endIndex} de ${total} registros para ${selectedPart.name}`
                        : 'Selecione uma peça para visualizar o histórico'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="part-logs-card">
            <div className="TabelaScroll">
              <div className="TabelaContent part-logs-content">
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo part-logs-grid">
                      <div className="Opcoes">Data/Hora</div>
                      <div className="Opcoes">Evento</div>
                      <div className="Opcoes">Quem fez</div>
                      <div className="Opcoes">Carro</div>
                      <div className="Opcoes">Detalhes</div>
                      <div className="Opcoes">Motivo</div>
                    </div>
                  </div>
                </div>

                {!selectedPartId && (
                  <div className="Tabela part-logs-grid">
                    <div className="Item" style={{ gridColumn: '1 / -1', justifyContent: 'center' }}>
                      <div className="Data">Selecione uma peça para visualizar o histórico</div>
                    </div>
                  </div>
                )}

                {/*
                  "Nenhum evento encontrado" é uma CONCLUSÃO sobre o histórico da peça — só
                  pode ser dita quando a consulta respondeu. Com erro, a tela diz que não
                  conseguiu verificar (at-60).
                */}
                {selectedPartId && erroLogs && logs.length === 0 && (
                  <div className="Tabela part-logs-grid">
                    <div className="Item" style={{ gridColumn: '1 / -1', justifyContent: 'center' }}>
                      <div className="Data">Não foi possível verificar o histórico desta peça.</div>
                    </div>
                  </div>
                )}

                {selectedPartId && !erroLogs && logs.length === 0 && (
                  <div className="Tabela part-logs-grid">
                    <div className="Item" style={{ gridColumn: '1 / -1', justifyContent: 'center' }}>
                      <div className="Data">Nenhum evento encontrado para os filtros informados</div>
                    </div>
                  </div>
                )}

                {selectedPartId && logs.map((log) => (
                  <div className="Tabela part-logs-grid" key={log.id}>
                    <div className="Item"><div className="Data">{new Date(log.createdAt).toLocaleString('pt-BR')}</div></div>
                    <div className="Item"><div className="Data part-logs-badge">{ACTION_LABEL[log.action] || log.action}</div></div>
                    <div className="Item"><div className="Data">{log.performedByName || '-'}</div></div>
                    <div className="Item"><div className="Data">{log.carPlate ? `${log.carPlate} - ${log.carModel || ''}` : '-'}</div></div>
                    <div className="Item"><div className="Data">{metadataSummary(log.metadata)}</div></div>
                    <div className="Item"><div className="Data">{log.reason || '-'}</div></div>
                  </div>
                ))}
              </div>
            </div>

            {selectedPartId && total > 0 && (
              <div className="PaginaO">
                <div
                  className={`Frame1000003950 ${page === 1 ? 'disabled' : ''}`}
                  onClick={() => page > 1 && setPage(page - 1)}
                  style={{ cursor: page === 1 ? 'not-allowed' : 'pointer' }}
                >
                  <img className="imgIcon" src={page === 1 ? ArrowLeftGray : ArrowLeftOrange} alt="Seta para Esquerda" width="24" height="24" />
                  <div className="Anterior" style={{ color: page === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}>Anterior</div>
                </div>

                <div className="Group21">
                  <div className="Lta2456">Página {page} de {totalPages}</div>
                </div>

                <div
                  className={`Frame1000003949 ${page >= totalPages ? 'disabled' : ''}`}
                  onClick={() => page < totalPages && setPage(page + 1)}
                  style={{ cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}
                >
                  <div className="PrXima" style={{ color: page >= totalPages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}>Próxima</div>
                  <img className="imgIcon" src={page >= totalPages ? ArrowRightGray : ArrowRightOrange} alt="Seta para Direita" width="24" height="24" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Log de Peças"
        secoes={helpSections}
      />
    </div>
  );
};

export default PartLogs;
